import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import MobileInput from "../Mobile/Inputs/MobileInput";
import MobileButton from "../Mobile/Buttons/MobileButton";
import { authError, clearChallenge, clearSession, getChallenge, loadCorporateSession, loginOtp, loginPassword, requestOtp, retryAfter, saveTokens, setChallenge } from "../../utils/auth-session";

export default function CorporateAccess({ initialMode = "password" }) {
    const navigate = useNavigate();
    const [pending, setPending] = useState(() => initialMode === "verify" ? getChallenge() : null);
    const [mode, setMode] = useState(initialMode === "password" ? "password" : "otp");
    const [email, setEmail] = useState(() => pending?.email || "");
    const [password, setPassword] = useState("");
    const [code, setCode] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [profilePending, setProfilePending] = useState(false);
    const [context, setContext] = useState(pending?.context || "login");
    const [blockedUntil, setBlockedUntil] = useState(0);
    const [now, setNow] = useState(Date.now());
    const active = useRef(0);
    const locked = useRef(false);
    useEffect(() => {
        const timer = setInterval(() => setNow(Date.now()), 1000);
        return () => { clearInterval(timer); active.current += 1; };
    }, []);
    const wait = Math.max(0, Math.ceil((blockedUntil - now) / 1000));
    const resendWait = Math.max(wait, Math.max(0, Math.ceil(((pending?.retryAt || 0) - now) / 1000)));
    const expires = Math.max(0, Math.ceil(((pending?.expiresAt || 0) - now) / 1000));

    const run = async operation => {
        if (locked.current || wait) return;
        locked.current = true;
        const sequence = ++active.current;
        const current = () => sequence === active.current;
        setBusy(true);
        setError("");
        try { await operation(current); }
        catch (err) {
            if (current()) {
                setError(authError(err));
                const delay = retryAfter(err);
                setNow(Date.now());
                setBlockedUntil(delay ? Date.now() + delay * 1000 : 0);
                if (!localStorage.getItem("my_code")) setProfilePending(false);
            }
        } finally {
            if (current()) { locked.current = false; setBusy(false); }
        }
    };

    const finish = async (current, nextContext) => {
        const destination = await loadCorporateSession(nextContext);
        if (current()) { clearChallenge(); navigate(destination, { replace: true }); }
    };

    const sendCode = () => run(async current => {
        const { data } = await requestOtp(email.trim());
        if (!current()) return;
        setPending(setChallenge(email.trim(), data, context));
        setNow(Date.now());
        setCode("");
    });

    const submit = event => {
        event.preventDefault();
        if (profilePending) return run(current => finish(current, context));
        if (!pending && mode === "otp") return sendCode();
        return run(async current => {
            let response;
            try {
                response = pending ? await loginOtp(pending.id, code) : await loginPassword(email.trim(), password);
            } catch (err) {
                if (pending && err.response?.status === 400 && current()) {
                    setCode("");
                    setPending(previous => ({ ...previous, expiresAt: 0 }));
                    throw new Error(`${authError(err)} Solicita otro código antes de verificar de nuevo.`);
                }
                // A timeout may have consumed the code. Never resubmit that code automatically.
                if (pending && (!err.response || err.response.status >= 500) && current()) {
                    setCode("");
                    setPending(previous => ({ ...previous, expiresAt: 0 }));
                    throw new Error("No pudimos confirmar la verificación. El código puede haberse usado; solicita otro cuando se habilite el reenvío.");
                }
                throw err;
            }
            if (!current()) return;
            clearSession();
            saveTokens(response.data);
            clearChallenge();
            setCode("");
            setPassword("");
            setPending(null);
            setProfilePending(true);
            await finish(current, context);
        });
    };

    const reset = nextMode => {
        active.current += 1;
        locked.current = false;
        clearChallenge();
        setPending(null); setCode(""); setPassword(""); setError("");
        setContext("login"); setMode(nextMode); setBusy(false);
    };

    return <div className="auth-access-scroll d-flex flex-column align-items-center w-100 gap-3">
        <h1 className="mobile-login-title mb-0">{profilePending ? "Preparando tu cuenta" : pending ? "Verifica tu correo" : mode === "otp" ? "Ingresa con un código" : "Bienvenido"}</h1>
        <p className="mobile-login-description mb-0 text-center">
            {profilePending ? "Tu acceso fue verificado. Estamos cargando tu perfil corporativo." : pending ? `Revisa la bandeja de entrada de ${pending.email} e ingresa el código de seis dígitos. Si no encuentras el correo, revisa la carpeta de spam o solicita un nuevo código.` : "Gestiona tu gimnasio y eleva la experiencia de tus atletas."}
        </p>
        {error && <div className="alert alert-danger w-100" role="alert">{error}</div>}
        <form className="w-100 d-flex flex-column gap-3" onSubmit={submit}>
            {!profilePending && (pending ? <>
                <MobileInput label="Código de seis dígitos" aria-label="Código de seis dígitos" name="code" value={code} onChange={e => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} required disabled={busy} />
                <p className="small text-muted mb-0">{expires ? `El código vence en ${Math.floor(expires / 60)}:${String(expires % 60).padStart(2, "0")}.` : "El código venció. Solicita uno nuevo."}</p>
            </> : <>
                <MobileInput label="Correo electrónico" aria-label="Correo electrónico" type="email" name="email" value={email} onChange={e => setEmail(e.target.value)} autoComplete="username" required disabled={busy} />
                {mode === "password" && <MobileInput label="Contraseña" aria-label="Contraseña" type="password" name="password" value={password} onChange={e => setPassword(e.target.value)} autoComplete="current-password" required disabled={busy} />}
            </>)}
            <MobileButton type="submit" text={wait ? `Espera ${wait} s` : profilePending ? "Reintentar cargar perfil" : pending ? "Verificar código" : mode === "otp" ? "Enviar código" : "Iniciar sesión"} loading={busy} disabled={!!wait || (!!pending && (!expires || code.length !== 6))} />
            {!profilePending && pending && <MobileButton text={resendWait ? `Reenviar en ${resendWait} s` : "Reenviar código"} variant="secondary" disabled={busy || !!resendWait} onClick={sendCode} />}
        </form>
        {!profilePending && <>
            <button type="button" className="auth-text-link" disabled={busy} onClick={() => reset(pending ? "otp" : mode === "otp" ? "password" : "otp")}>
                {pending ? "Cambiar correo" : mode === "otp" ? "Ingresar con contraseña" : "Ingresar con código / Olvidé mi contraseña"}
            </button>
            <hr className="auth-signup-divider" />
            <MobileButton
                text="Registra tu cuenta aquí"
                variant="secondary"
                className="auth-signup-button"
                disabled={busy}
                onClick={() => { clearChallenge(); navigate("/auth/signup"); }}
            />
        </>}
    </div>;
}
